import { z } from "zod"

import { toStringApiErrorSchema } from "@/shared/api/errors"
import { departmentSchema } from "@/shared/store/referential/referential"

export const loginRequestSchema = z.object({
	email: z.string().email(),
	password: z.string()
})
export type LoginRequest = z.infer<typeof loginRequestSchema>

export const registerRequestSchema = z.object({
	first_name: z.string().min(1, "Prénom requis"),
	last_name: z.string().min(1, "Nom requis"),
	login: z.string().min(3, "Login d'au moins 3 caractères"),
	email: z.string().email("Email invalide"),
	password: z.string().min(8, "Mot de passe d'au moins 8 caractères")
})
export type RegisterRequest = z.infer<typeof registerRequestSchema>

export const forgotPasswordRequestSchema = z.object({
	email: z.string().email("Email invalide")
})
export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequestSchema>

export const resetPasswordRequestSchema = z.object({
	token: z.string(),
	new_password: z.string().min(8, "Mot de passe d'au moins 8 caractères")
})
export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>
export const ROLES = ["volunteer", "admin"] as const
export const roleSchema = z.enum(ROLES)

export type Role = z.infer<typeof roleSchema>

export const offlineMeSchema = z.object({
	favorites: z.string().array()
})
const connectedMeBaseSchema = z
	.object({
		id: z.string(),
		login: z.string(),
		email: z.string(),
		avatarUrl: z.string().url().optional(),
		departments: z.array(z.string()).optional()
	})
	.extend(offlineMeSchema.shape)

const volunteerResponseSchema = z.object({
	role: roleSchema.extract(["volunteer"])
})
const administratorResponseSchema = z.object({
	role: roleSchema.extract(["admin"])
})
const rolePropertiesSchema = z.discriminatedUnion("role", [
	volunteerResponseSchema,
	administratorResponseSchema
])

export const meResponseSchema = connectedMeBaseSchema
	.and(rolePropertiesSchema)
	.and(offlineMeSchema)

export const updateMeRequestSchema = z.object({
	favorites: z.string().array()
})
const enrichedVolunteerSchema = z.object({
	role: roleSchema.extract(["volunteer"])
})

export const connectedMeSchema = connectedMeBaseSchema
	.omit({ departments: true })
	.and(z.object({ departments: departmentSchema.array() }))
	.and(
		z.discriminatedUnion("role", [
			enrichedVolunteerSchema,
			administratorResponseSchema
		])
	)
export const meSchema = connectedMeSchema.or(offlineMeSchema)
export const tokenSchema = z.object({
	accessToken: z.jwt(),
	refreshToken: z.jwt()
})
export type Me = z.infer<typeof meSchema>
export type ConnectedMe = z.infer<typeof connectedMeSchema>
export type OfflineMe = z.infer<typeof offlineMeSchema>
export type MeResponse = z.infer<typeof meResponseSchema>
export type UpdateMeRequest = z.infer<typeof updateMeRequestSchema>
export type TokenResponse = z.infer<typeof tokenSchema>
export type Volunteer = MeResponse & { role: "volunteer" }
export type Administrator = MeResponse & { role: "admin" }

export const credentialErrorSchema = toStringApiErrorSchema(
	z.literal(["INVALID_CREDENTIALS", "USER_INACTIVE"])
)

export type CredentialError = z.infer<typeof credentialErrorSchema>
export const registerErrorSchema = toStringApiErrorSchema(
	z.literal("USER_ALREADY_EXISTS")
)
export type RegisterError = z.infer<typeof registerErrorSchema>
export const invalidTokenSchema = toStringApiErrorSchema(
	z.literal("INVALID_TOKEN")
)
export type TokenError = z.infer<typeof invalidTokenSchema>

export const meErrorSchema = z.union([
	credentialErrorSchema,
	invalidTokenSchema
])
export type MeError = z.infer<typeof meErrorSchema>
